import React, { useEffect } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { setLanguage } from 'actions'
import logo from 'components/styled/assets/recright-logo.svg'
import { Flex } from 'components/styled/containers/flex'
import { PageContent } from 'components/styled/page/page-content'
import { languageList } from 'i18n'
import { mapLanguageToComponentProps } from 'admin/i18n'
import { useLocation, useNavigate } from 'react-router-dom'
import { MenuButton } from 'components/styled/menu/menu-item'
import { PopoverMenu } from 'components/styled/popover/menu'

interface LanguageProps {
  code: string
  label: string
}

interface PublicAdminFormsWrapperProps {
  children: React.ReactNode
  currentLanguage: string
  dispatch: any
}

const StyledPageContent = styled(PageContent)`
  position: relative;
  overflow: visible;
  align-items: center;
  display: flex;
  flex-direction: column;
`

const StyledPopoverMenu = styled(PopoverMenu)`
  button {
    text-transform: uppercase;
  }
`

const MenuWrapper = styled.div`
  position: absolute;
  right: 0;
  z-index: 1;
  display: flex;
  align-items: flex-end;
  flex-direction: column;
`

const LogoWrapper = styled.div`
  margin: ${({ theme }) => theme.space.s8} 0;
`

const HeaderWrapper = styled(Flex)`
  position: relative;
  justify-content: center;
  width: ${({ theme }) => theme.space.s800};
  margin-bottom: ${({ theme }) => theme.space.s40};

  ${({ theme }) => theme.mediaQuery.tablet} {
    width: 100%;
    justify-content: space-between;
  }
`

const StyledContainer = styled(Flex)`
  padding-top: ${({ theme }) => theme.space.s48};

  ${({ theme }) => theme.mediaQuery.phone} {
    padding-top: ${({ theme }) => theme.space.s16};
  }
`

const mapStateToProps = (state: any) => ({
  currentLanguage: state.global.language,
})

export const PublicAdminFormsWrapper = ({
  children,
  dispatch,
  currentLanguage,
}: PublicAdminFormsWrapperProps) => {
  const languages = languageList()
  const location = useLocation()
  const navigate = useNavigate()
  const current = languages.find(l => l.code === currentLanguage)

  const onSetLanguage = (language: string) => {
    const path = location.pathname
    const newPath = path.replace(`/${currentLanguage}/`, `/${language}/`)
    navigate(newPath, { replace: true })
    dispatch(setLanguage(language))
  }

  useEffect(() => {
    const path = location.pathname
    const langFromUrl = path.split('/')[2]
    const urlHasLanguage = languageList().find(
      lang => lang.code === langFromUrl
    )
    if (urlHasLanguage) {
      onSetLanguage(langFromUrl)
    }
  }, [])

  const languageMenu = (
    <MenuWrapper>
      <StyledPopoverMenu
        triggerButtonProps={{
          icon: 'language',
          text: current ? current.code : '',
          variant: 'primaryBasic',
          textVariant: 'body',
        }}
      >
        {languages.map((language: LanguageProps) => (
          <MenuButton
            key={language.code}
            isSelected={language.code === currentLanguage}
            text={language.label}
            iconRight={language.code === currentLanguage ? 'check' : undefined}
            onClick={() => onSetLanguage(language.code)}
          />
        ))}
      </StyledPopoverMenu>
    </MenuWrapper>
  )

  return (
    <>
      <StyledContainer
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
      >
        <StyledPageContent size="xSmall">
          <HeaderWrapper mb="s40">
            <LogoWrapper>
              <img src={logo} width={120} alt="logo-recright" />
            </LogoWrapper>
            {languageMenu}
          </HeaderWrapper>
          {children}
        </StyledPageContent>
      </StyledContainer>
    </>
  )
}

export default connect(mapLanguageToComponentProps(mapStateToProps))(PublicAdminFormsWrapper)
